/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import { useUserPreferences } from '../context/UserPreferencesContext';

const STORAGE_KEY = 'user:prefs';
const DEFAULT_PREFS = {
  timezone: 'UTC',
  locale: 'en-US',
};

function parseDateLike(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getStoredPrefs() {
  if (typeof window === 'undefined') return DEFAULT_PREFS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      timezone: parsed?.timezone || DEFAULT_PREFS.timezone,
      locale: parsed?.locale || DEFAULT_PREFS.locale,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function formatStoredWithPrefs(value, options, fallback = '') {
  const date = parseDateLike(value);
  if (!date) return fallback;

  const prefs = getStoredPrefs();

  try {
    return new Intl.DateTimeFormat(prefs.locale, {
      timeZone: prefs.timezone,
      ...(options || {}),
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

export function formatStoredDateTime(value, options, fallback = '') {
  return formatStoredWithPrefs(value, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...(options || {}),
  }, fallback);
}

export function formatStoredDate(value, options, fallback = '') {
  return formatStoredWithPrefs(value, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    ...(options || {}),
  }, fallback);
}

export function formatStoredTime(value, options, fallback = '') {
  return formatStoredWithPrefs(value, {
    hour: '2-digit',
    minute: '2-digit',
    ...(options || {}),
  }, fallback);
}

// Hook helpers to format with global timezone/locale
export function useDateTimeFormatting() {
  const prefs = useUserPreferences();
  return {
    formatDate: prefs?.formatDate,
    formatDateTime: prefs?.formatDateTime,
  };
}

export function useNumberFormatting() {
  const prefs = useUserPreferences();
  return {
    formatNumber: prefs?.formatNumber,
    formatCurrency: prefs?.formatCurrency,
  };
}
